import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { AttachmentMenu } from './AttachmentMenu';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onStopStreaming?: () => void;
  isLoading?: boolean;
  isStreaming?: boolean;
  placeholder?: string;
  suggestions?: string[];
  onAddImages?: () => void;
  onAddBooks?: () => void;
  showAttachmentMenu?: boolean;
  attachments?: Array<{
    type: 'image';
    data_url: string;
    file_name: string;
    mime_type: string;
  }>;
  onRemoveAttachment?: (index: number) => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onStopStreaming,
  isLoading = false,
  isStreaming = false,
  placeholder = "Ask me anything...",
  suggestions = [],
  onAddImages,
  onAddBooks,
  showAttachmentMenu = false,
  attachments = [],
  onRemoveAttachment
}) => {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const handleSend = () => {
    if (input.trim() && !isLoading && !isStreaming) {
      onSendMessage(input.trim());
      setInput('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  return (
    <div className="chat-input-wrapper">
      <div className="input-container">
        {showAttachmentMenu && onAddImages && onAddBooks && (
          <div className="attachment-menu-container">
            <AttachmentMenu
              onAddImages={onAddImages}
              onAddBooks={onAddBooks}
            />
          </div>
        )}

        {/* Image Preview Section */}
        {attachments && attachments.length > 0 && (
          <div className="image-preview-container">
            {attachments.map((attachment, index) => (
              <div key={index} className="preview-item">
                <img src={attachment.data_url} alt={attachment.file_name} title={attachment.file_name} />
                {onRemoveAttachment && (
                  <button
                    onClick={() => onRemoveAttachment(index)}
                    className="remove-preview-btn"
                    type="button"
                    aria-label={`Remove ${attachment.file_name}`}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading || isStreaming}
          className="chat-textarea"
          rows={1}
        />

        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading || isStreaming}
          className={`send-button send ${!input.trim() || isLoading || isStreaming ? 'disabled' : ''}`}
        >
          <PaperAirplaneIcon className="button-icon" />
        </button>
      </div>

      {/* Character count */}
      {input.length > 500 && (
        <div className="character-count">
          {input.length} / 4000
        </div>
      )}

      <style>{`
        .chat-input-wrapper {
          width: 100%;
        }

        .input-container {
          position: relative;
          width: 100%;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 24px;
          transition: all 0.2s;
        }

        .input-container:focus-within {
          border-color: var(--accent-primary);
          box-shadow: var(--accent-glow);
        }

        .attachment-menu-container {
          position: absolute;
          left: 12px;
          bottom: 10px;
          z-index: 10;
        }

        /* Image Preview Container */
        .image-preview-container {
          padding: 12px 12px 8px 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          max-height: 160px;
          overflow-y: auto;
          border-bottom: 1px solid var(--border-color);
        }

        .preview-item {
          position: relative;
          width: 60px;
          height: 60px;
          border-radius: 8px;
          overflow: hidden;
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          flex-shrink: 0;
        }

        .preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .remove-preview-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #EF4444;
          color: white;
          border: 1px solid white;
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
          padding: 0;
        }

        .preview-item:hover .remove-preview-btn {
          opacity: 1;
        }

        /* Scrollbar for image preview */
        .image-preview-container::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        .image-preview-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .image-preview-container::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 999px;
        }

        .image-preview-container::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }

        .chat-textarea {
          width: 100%;
          padding: 14px 52px 14px 20px;
          background: transparent;
          border: none;
          resize: none;
          outline: none;
          font-size: 15px;
          line-height: 1.5;
          color: var(--text-primary);
          max-height: 200px;
          overflow-y: auto;
          font-family: inherit;
          scrollbar-width: none;        /* Firefox */
          -ms-overflow-style: none;     /* IE/Edge */
        }

        .chat-textarea::-webkit-scrollbar {
          display: none;                /* Chrome, Safari, Opera */
        }

        /* When attachment menu is present, add left padding */
        .input-container:has(.attachment-menu-container) .chat-textarea {
          padding-left: 52px;
        }

        .chat-textarea::placeholder {
          color: var(--text-tertiary);
        }

        /* Center placeholder when empty */
        .chat-textarea:placeholder-shown {
          text-align: center;
        }

        .chat-textarea:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Send/Stop Button */
        .send-button {
          position: absolute;
          right: 8px;
          bottom: 8px;
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .send-button.send {
          background: var(--accent-primary);
          color: white;
        }

        .send-button.send:hover:not(.disabled) {
          background: var(--accent-primary-darker);
          transform: scale(1.05);
        }

        .send-button.disabled {
          background: var(--bg-tertiary);
          color: var(--text-tertiary);
          cursor: not-allowed;
        }

        .send-button.disabled:hover {
          transform: none;
        }

        .button-icon {
          width: 20px;
          height: 20px;
        }

        /* Character Count */
        .character-count {
          text-align: right;
          font-size: 12px;
          color: var(--text-tertiary);
          margin-top: 4px;
          padding-right: 8px;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .chat-textarea {
            padding: 12px 48px 12px 16px;
            font-size: 16px; /* Prevents zoom on iOS */
          }

          .send-button {
            width: 32px;
            height: 32px;
            right: 6px;
            bottom: 6px;
          }

          .button-icon {
            width: 18px;
            height: 18px;
          }

          .image-preview-container {
            padding: 8px 8px 6px 8px;
            gap: 6px;
          }

          .preview-item {
            width: 50px;
            height: 50px;
          }

          .remove-preview-btn {
            width: 18px;
            height: 18px;
            font-size: 12px;
            top: 2px;
            right: 2px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .input-container,
          .send-button {
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ChatInput;